<script setup lang="ts">
import { ImageOff, ImagePlus, Loader2 } from "@lucide/vue";
import { useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import type { CompanyPhoto } from "@/entities/company";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  FormAlert,
  Skeleton,
} from "@/shared/ui";

import type { GalleryErrorKey } from "../model/mapGalleryError";
import { useSalonGallery } from "../model/useSalonGallery";
import SalonGalleryTile from "./SalonGalleryTile.vue";

const props = defineProps<{
  photos: CompanyPhoto[];
  galleryStatus: "loading" | "ready" | "error";
}>();

const emit = defineEmits<{
  retry: [];
  changed: [photos: CompanyPhoto[]];
  announce: [msg: string];
}>();

const { t } = useI18n();

const {
  pendingUploads,
  uploadErrorKey,
  removeState,
  removeErrorKey,
  reorderInFlight,
  isFull,
  uploadFile,
  requestRemove,
  cancelRemove,
  confirmRemove,
  moveLeft,
  moveRight,
} = useSalonGallery({
  getPhotos: () => props.photos,
  setPhotos: (next) => emit("changed", next),
  announce: (msg) => emit("announce", msg),
});

const fileInputRef = useTemplateRef<HTMLInputElement>("fileInput");

function onFileChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    void uploadFile(file);
    // reset so the same file can be re-selected after an error
    input.value = "";
  }
}

function triggerFileInput(): void {
  fileInputRef.value?.click();
}
</script>

<template>
  <!-- Loading state -->
  <div
    v-if="galleryStatus === 'loading'"
    class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
    aria-busy="true"
    :aria-label="t('salon.gallery.title')"
  >
    <span class="sr-only">{{ t('salon.gallery.title') }}</span>
    <Skeleton
      v-for="i in 4"
      :key="i"
      class="aspect-square rounded-md"
    />
  </div>

  <!-- Error state -->
  <div
    v-else-if="galleryStatus === 'error'"
    class="flex flex-col gap-3"
  >
    <FormAlert
      variant="destructive"
      :message="t('salon.gallery.loadError')"
    />
    <Button
      variant="outline"
      size="sm"
      class="self-start"
      @click="emit('retry')"
    >
      {{ t('salon.retry') }}
    </Button>
  </div>

  <!-- Ready state -->
  <div
    v-else
    class="flex flex-col gap-3"
  >
    <!-- Single file input — always in DOM, visually hidden -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png"
      class="sr-only"
      @change="onFileChange"
    />

    <!-- Upload inline error -->
    <FormAlert
      v-if="uploadErrorKey"
      variant="destructive"
      :message="t(uploadErrorKey as GalleryErrorKey)"
    />

    <!-- Empty state (no photos, no pending uploads) -->
    <div
      v-if="photos.length === 0 && pendingUploads.length === 0"
      class="flex flex-col items-center gap-4 py-10 text-muted-foreground"
    >
      <ImageOff
        class="size-10"
        aria-hidden="true"
      />
      <p class="text-sm">{{ t('salon.gallery.empty') }}</p>
      <Button
        variant="outline"
        size="sm"
        @click="triggerFileInput"
      >
        <ImagePlus aria-hidden="true" />
        {{ t('salon.gallery.addPhoto') }}
      </Button>
    </div>

    <!-- Photo grid -->
    <template v-else>
      <div
        role="list"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
      >
        <!-- Confirmed photos -->
        <SalonGalleryTile
          v-for="(photo, idx) in photos"
          :key="photo.id"
          :photo="photo"
          :index="idx"
          :total="photos.length"
          :is-first="idx === 0"
          :is-last="idx === photos.length - 1"
          :reorder-in-flight="reorderInFlight"
          @move-left="moveLeft(photo.id)"
          @move-right="moveRight(photo.id)"
          @delete="requestRemove(photo.id)"
        />

        <!-- Pending upload tiles -->
        <div
          v-for="pending in pendingUploads"
          :key="pending.tempId"
          role="listitem"
          class="relative aspect-square overflow-hidden rounded-md border border-border"
          aria-busy="true"
        >
          <img
            :src="pending.previewUrl"
            class="size-full object-cover opacity-60"
            alt=""
            aria-hidden="true"
          />
          <div class="absolute inset-0 flex items-center justify-center">
            <Loader2
              v-if="pending.status === 'uploading'"
              class="size-6 animate-spin text-primary motion-reduce:animate-none"
              aria-hidden="true"
            />
          </div>
        </div>

        <!-- Add photo tile (hidden when gallery is full) -->
        <div
          v-if="!isFull"
          role="listitem"
          class="aspect-square"
        >
          <button
            type="button"
            class="flex size-full items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            :aria-label="t('salon.gallery.addPhoto')"
            @click="triggerFileInput"
          >
            <ImagePlus
              class="size-6"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      <!-- Gallery full hint -->
      <p
        v-if="isFull"
        class="text-xs text-muted-foreground"
      >
        {{ t('salon.gallery.full') }}
      </p>
    </template>
  </div>

  <!-- Delete confirmation dialog -->
  <AlertDialog
    :open="removeState === 'confirming' || removeState === 'removing'"
    @update:open="(v) => { if (!v && removeState !== 'removing') cancelRemove() }"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('salon.gallery.deleteConfirm.title') }}</AlertDialogTitle>
        <AlertDialogDescription>{{ t('salon.gallery.deleteConfirm.description') }}</AlertDialogDescription>
      </AlertDialogHeader>

      <FormAlert
        v-if="removeErrorKey"
        variant="destructive"
        :message="t(removeErrorKey as GalleryErrorKey)"
      />

      <AlertDialogFooter>
        <AlertDialogCancel @click="cancelRemove">
          {{ t('salon.gallery.deleteConfirm.cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          variant="destructive"
          :loading="removeState === 'removing'"
          @click="confirmRemove"
        >
          {{ t('salon.gallery.deleteConfirm.confirm') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
